// ============================================================
// v2 API — /v2/projects/:projectId/{reports/*, config, sub-tenants,
// goals, funnels, destinations, deliveries}. The dashboard JWT is a
// valid v2 principal for members of the project's organization.
// Response shapes + mapping live in ./v2Mappers (pure, unit tested).
// ============================================================

import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { showNotification } from '@/app/notificationSlice'
import {
    isMfaRequiredError,
    isOrgSuspendedError,
    logout,
    setMfaRequired,
    setOrgSuspended,
} from '@/modules/auth/authSlice'

import {
    deriveV2BaseUrl,
    mapBreakdown,
    mapEventsReport,
    mapOverview,
    mapTimeseries,
    mapUsage,
    parseNdjson,
    reportQuery,
    unwrap,
    type AttributionReport,
    type BreakdownResponse,
    type BreakdownRow,
    type DeliveriesResponse,
    type Destination,
    type DestinationInput,
    type DestinationTestResult,
    type EcommerceReport,
    type ErrorsReport,
    type EventMatcher,
    type FunnelDef,
    type FunnelReport,
    type GoalDef,
    type GoalsReport,
    type Overview,
    type PerformanceReport,
    type ProjectConfigResponse,
    type RawEvent,
    type RealtimeReport,
    type RetentionReport,
    type SeriesPoint,
    type SubTenant,
    type TimeseriesResponse,
    type UsageReport,
    type UsageSummary,
    type V2Envelope,
    type V2RangeArgs,
} from './v2Mappers'
import type {
    DigestRunResult,
    InviteResult,
    Portal,
    PortalBranding,
    PortalUser,
    PortalUserCreated,
    ScheduledReport,
    ScheduledReportInput,
} from './portalTypes'

export const V2_BASE_URL: string =
    import.meta.env.VITE_API_V2_URL ?? deriveV2BaseUrl(import.meta.env.VITE_API_URL)

const rawBaseQuery = fetchBaseQuery({
    baseUrl: V2_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as { auth: { token: string | null } }).auth.token
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    },
})

const v2BaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    const result = await rawBaseQuery(args, api, extraOptions)
    if (result.error && result.error.status === 401) {
        api.dispatch(logout())
        api.dispatch(
            showNotification({
                message: 'Your session has expired. Please log in again.',
                severity: 'warning',
            }),
        )
    }
    if (result.error && isMfaRequiredError(result.error.status, result.error.data)) {
        api.dispatch(setMfaRequired(true))
    }
    if (result.error && isOrgSuspendedError(result.error.status, result.error.data)) {
        api.dispatch(setOrgSuspended(true))
    }
    return result
}

const p = (projectId: string) => `/projects/${encodeURIComponent(projectId)}`

export const v2Api = createApi({
    reducerPath: 'v2',
    baseQuery: v2BaseQuery,
    tagTypes: [
        'V2Config',
        'SubTenants',
        'Goals',
        'Funnels',
        'Destinations',
        'Deliveries',
        'Portals',
        'PortalUsers',
        'ScheduledReports',
        'ErrorGroups',
        'ReconSources',
        'Reconciliation',
        'Exports',
        'ExportRuns',
        'ManagementKeys',
        'ProjectKeys',
        'PrivacyRequests',
        'ConnectInstalls',
    ],
    endpoints: builder => ({
        // ---------- reports ----------
        getOverview: builder.query<Overview, V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/overview?${reportQuery(a)}`,
            transformResponse: mapOverview,
        }),
        getTimeseries: builder.query<
            SeriesPoint[],
            V2RangeArgs & { metric: string; interval: 'hour' | 'day' | 'week' | 'month' }
        >({
            query: a =>
                `${p(a.projectId)}/reports/timeseries?${reportQuery(a, { metric: a.metric, interval: a.interval })}`,
            transformResponse: (res: V2Envelope<TimeseriesResponse>) => mapTimeseries(res),
        }),
        getBreakdown: builder.query<
            BreakdownRow[],
            V2RangeArgs & { dim: string; event?: string; limit?: number }
        >({
            query: a =>
                `${p(a.projectId)}/reports/breakdown?${reportQuery(a, { dim: a.dim, event: a.event, limit: a.limit })}`,
            transformResponse: (res: V2Envelope<BreakdownResponse>) => mapBreakdown(res),
        }),
        getEventsReport: builder.query<BreakdownRow[], V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/events?${reportQuery(a)}`,
            transformResponse: mapEventsReport,
        }),
        getEcommerce: builder.query<EcommerceReport, V2RangeArgs & { limit?: number }>({
            query: a => `${p(a.projectId)}/reports/ecommerce?${reportQuery(a, { limit: a.limit })}`,
            transformResponse: (res: V2Envelope<EcommerceReport>) => unwrap(res),
        }),
        getGoalsReport: builder.query<GoalsReport, V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/goals?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<GoalsReport>) => unwrap(res),
            providesTags: ['Goals'],
        }),
        getFunnelReport: builder.query<FunnelReport, V2RangeArgs & { id: string }>({
            query: a => `${p(a.projectId)}/reports/funnel?${reportQuery(a, { id: a.id })}`,
            transformResponse: (res: V2Envelope<FunnelReport>) => unwrap(res),
            providesTags: ['Funnels'],
        }),
        /** Ad-hoc funnel (builder preview) — POST body carries steps + range. */
        previewFunnel: builder.mutation<
            FunnelReport,
            V2RangeArgs & { steps: EventMatcher[]; window_days: number; by: 'visitor' | 'session' }
        >({
            query: ({ projectId, from, to, subTenant, ...def }) => ({
                url: `${p(projectId)}/reports/funnel?${reportQuery({ projectId, from, to, subTenant })}`,
                method: 'POST',
                body: def,
            }),
            transformResponse: (res: V2Envelope<FunnelReport>) => unwrap(res),
        }),
        getAttribution: builder.query<
            AttributionReport,
            V2RangeArgs & {
                conversion: string
                touch: string
                model: 'any_touch' | 'first_touch' | 'last_touch'
                windowDays: number
            }
        >({
            query: a =>
                `${p(a.projectId)}/reports/attribution?${reportQuery(a, {
                    conversion: a.conversion,
                    touch: a.touch,
                    model: a.model,
                    window_days: a.windowDays,
                })}`,
            transformResponse: (res: V2Envelope<AttributionReport>) => unwrap(res),
        }),
        getRetention: builder.query<
            RetentionReport,
            V2RangeArgs & { interval: 'day' | 'week'; periods: number; event?: string }
        >({
            query: a =>
                `${p(a.projectId)}/reports/retention?${reportQuery(a, {
                    interval: a.interval,
                    periods: a.periods,
                    event: a.event,
                })}`,
            transformResponse: (res: V2Envelope<RetentionReport>) => unwrap(res),
        }),
        getPerformance: builder.query<PerformanceReport, V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/performance?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<PerformanceReport>) => unwrap(res),
        }),
        getErrors: builder.query<ErrorsReport, V2RangeArgs & { limit?: number }>({
            query: a => `${p(a.projectId)}/reports/errors?${reportQuery(a, { limit: a.limit })}`,
            transformResponse: (res: V2Envelope<ErrorsReport>) => unwrap(res),
        }),
        getRealtime: builder.query<
            RealtimeReport,
            { projectId: string; subTenant?: string | null; minutes?: number }
        >({
            query: a => {
                const q = new URLSearchParams({ minutes: String(a.minutes ?? 30) })
                if (a.subTenant !== undefined && a.subTenant !== null) {
                    q.set('st', a.subTenant === '' ? '-' : a.subTenant)
                }
                return `${p(a.projectId)}/reports/realtime?${q.toString()}`
            },
            transformResponse: (res: V2Envelope<RealtimeReport>) => unwrap(res),
        }),
        getUsage: builder.query<UsageSummary, V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/usage?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<UsageReport>) => mapUsage(res),
        }),
        /** Most recent raw events (newest first) via /export as NDJSON. */
        getRecentEvents: builder.query<RawEvent[], V2RangeArgs & { limit?: number }>({
            query: a => ({
                url: `${p(a.projectId)}/export?${reportQuery(a, { format: 'ndjson', limit: a.limit ?? 200 })}`,
                responseHandler: 'text',
            }),
            transformResponse: (text: string) => parseNdjson(text),
        }),

        // ---------- management ----------
        getProjectConfig: builder.query<ProjectConfigResponse, string>({
            query: projectId => `${p(projectId)}/config`,
            transformResponse: (res: V2Envelope<ProjectConfigResponse>) => unwrap(res),
            providesTags: ['V2Config'],
        }),
        listSubTenants: builder.query<SubTenant[], { projectId: string; after?: string }>({
            query: a =>
                `${p(a.projectId)}/sub-tenants?limit=1000${a.after ? `&after=${encodeURIComponent(a.after)}` : ''}`,
            transformResponse: (res: V2Envelope<SubTenant[]>) => unwrap(res),
            providesTags: ['SubTenants'],
        }),
        listGoals: builder.query<GoalDef[], string>({
            query: projectId => `${p(projectId)}/goals`,
            transformResponse: (res: V2Envelope<GoalDef[]>) => unwrap(res),
            providesTags: ['Goals'],
        }),
        createGoal: builder.mutation<
            GoalDef,
            { projectId: string; name: string; match: EventMatcher }
        >({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/goals`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<GoalDef>) => unwrap(res),
            invalidatesTags: ['Goals'],
        }),
        deleteGoal: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/goals/${encodeURIComponent(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Goals'],
        }),
        listFunnels: builder.query<FunnelDef[], string>({
            query: projectId => `${p(projectId)}/funnels`,
            transformResponse: (res: V2Envelope<FunnelDef[]>) => unwrap(res),
            providesTags: ['Funnels'],
        }),
        createFunnel: builder.mutation<
            FunnelDef,
            {
                projectId: string
                name: string
                steps: EventMatcher[]
                window_days: number
                by: 'visitor' | 'session'
            }
        >({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/funnels`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<FunnelDef>) => unwrap(res),
            invalidatesTags: ['Funnels'],
        }),
        deleteFunnel: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/funnels/${encodeURIComponent(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Funnels'],
        }),
        listDestinations: builder.query<Destination[], string>({
            query: projectId => `${p(projectId)}/destinations`,
            transformResponse: (res: V2Envelope<Destination[]>) => unwrap(res),
            providesTags: ['Destinations'],
        }),
        createDestination: builder.mutation<Destination, { projectId: string } & DestinationInput>({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/destinations`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<Destination>) => unwrap(res),
            invalidatesTags: ['Destinations'],
        }),
        updateDestination: builder.mutation<
            Destination,
            {
                projectId: string
                id: string
                active?: boolean
                rotate_secret?: boolean
            } & Record<string, unknown>
        >({
            query: ({ projectId, id, ...body }) => ({
                url: `${p(projectId)}/destinations/${encodeURIComponent(id)}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: V2Envelope<Destination>) => unwrap(res),
            invalidatesTags: ['Destinations'],
        }),
        deleteDestination: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/destinations/${encodeURIComponent(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Destinations'],
        }),
        testDestination: builder.mutation<DestinationTestResult, { projectId: string; id: string }>(
            {
                query: a => ({
                    url: `${p(a.projectId)}/destinations/${encodeURIComponent(a.id)}/test`,
                    method: 'POST',
                }),
                transformResponse: (res: V2Envelope<DestinationTestResult>) => unwrap(res),
                invalidatesTags: ['Deliveries'],
            },
        ),
        getDeliveries: builder.query<DeliveriesResponse, { projectId: string; id: string }>({
            query: a =>
                `${p(a.projectId)}/destinations/${encodeURIComponent(a.id)}/deliveries?limit=50`,
            transformResponse: (res: V2Envelope<DeliveriesResponse>) => unwrap(res),
            providesTags: ['Deliveries'],
        }),
        redeliver: builder.mutation<unknown, { projectId: string; deliveryId: string }>({
            query: a => ({
                url: `${p(a.projectId)}/deliveries/${encodeURIComponent(a.deliveryId)}/redeliver`,
                method: 'POST',
            }),
            invalidatesTags: ['Deliveries'],
        }),

        // ---------- client portals (owner/admin) ----------
        listPortals: builder.query<Portal[], string>({
            query: projectId => `${p(projectId)}/portals`,
            transformResponse: (res: V2Envelope<Portal[]>) => unwrap(res),
            providesTags: ['Portals'],
        }),
        createPortal: builder.mutation<
            Portal,
            {
                projectId: string
                slug: string
                branding?: Partial<PortalBranding>
                reports?: string[]
            }
        >({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/portals`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<Portal>) => unwrap(res),
            invalidatesTags: ['Portals'],
        }),
        updatePortal: builder.mutation<
            Portal,
            {
                projectId: string
                id: string
                branding?: Partial<PortalBranding>
                reports?: string[]
                status?: 'active' | 'disabled'
            }
        >({
            query: ({ projectId, id, ...body }) => ({
                url: `${p(projectId)}/portals/${encodeURIComponent(id)}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: V2Envelope<Portal>) => unwrap(res),
            invalidatesTags: ['Portals'],
        }),
        deletePortal: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/portals/${encodeURIComponent(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Portals', 'PortalUsers', 'ScheduledReports'],
        }),
        setPortalDomain: builder.mutation<
            Portal,
            { projectId: string; id: string; hostname: string }
        >({
            query: ({ projectId, id, hostname }) => ({
                url: `${p(projectId)}/portals/${encodeURIComponent(id)}/domain`,
                method: 'PUT',
                body: { hostname },
            }),
            transformResponse: (res: V2Envelope<Portal>) => unwrap(res),
            invalidatesTags: ['Portals'],
        }),
        refreshPortalDomain: builder.mutation<Portal, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/portals/${encodeURIComponent(a.id)}/domain/refresh`,
                method: 'POST',
            }),
            transformResponse: (res: V2Envelope<Portal>) => unwrap(res),
            invalidatesTags: ['Portals'],
        }),
        deletePortalDomain: builder.mutation<Portal, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/portals/${encodeURIComponent(a.id)}/domain`,
                method: 'DELETE',
            }),
            transformResponse: (res: V2Envelope<Portal>) => unwrap(res),
            invalidatesTags: ['Portals'],
        }),
        listPortalUsers: builder.query<PortalUser[], { projectId: string; portalId: string }>({
            query: a => `${p(a.projectId)}/portals/${encodeURIComponent(a.portalId)}/users`,
            transformResponse: (res: V2Envelope<PortalUser[]>) => unwrap(res),
            providesTags: ['PortalUsers'],
        }),
        createPortalUser: builder.mutation<
            PortalUserCreated,
            {
                projectId: string
                portalId: string
                email: string
                name?: string
                sub_tenants: string[]
            }
        >({
            query: ({ projectId, portalId, ...body }) => ({
                url: `${p(projectId)}/portals/${encodeURIComponent(portalId)}/users`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<PortalUserCreated>) => unwrap(res),
            invalidatesTags: ['PortalUsers'],
        }),
        updatePortalUser: builder.mutation<
            PortalUser,
            {
                projectId: string
                portalId: string
                userId: string
                sub_tenants?: string[]
                status?: 'active' | 'disabled'
                name?: string
            }
        >({
            query: ({ projectId, portalId, userId, ...body }) => ({
                url: `${p(projectId)}/portals/${encodeURIComponent(portalId)}/users/${encodeURIComponent(userId)}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: V2Envelope<PortalUser>) => unwrap(res),
            invalidatesTags: ['PortalUsers'],
        }),
        resendPortalInvite: builder.mutation<
            InviteResult,
            { projectId: string; portalId: string; userId: string }
        >({
            query: a => ({
                url: `${p(a.projectId)}/portals/${encodeURIComponent(a.portalId)}/users/${encodeURIComponent(a.userId)}/invite`,
                method: 'POST',
            }),
            transformResponse: (res: V2Envelope<InviteResult>) => unwrap(res),
            invalidatesTags: ['PortalUsers'],
        }),
        deletePortalUser: builder.mutation<
            unknown,
            { projectId: string; portalId: string; userId: string }
        >({
            query: a => ({
                url: `${p(a.projectId)}/portals/${encodeURIComponent(a.portalId)}/users/${encodeURIComponent(a.userId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalUsers'],
        }),

        // ---------- scheduled reports (write scope) ----------
        listScheduledReports: builder.query<ScheduledReport[], string>({
            query: projectId => `${p(projectId)}/scheduled-reports`,
            transformResponse: (res: V2Envelope<ScheduledReport[]>) => unwrap(res),
            providesTags: ['ScheduledReports'],
        }),
        createScheduledReport: builder.mutation<
            ScheduledReport,
            ScheduledReportInput & { projectId: string }
        >({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/scheduled-reports`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<ScheduledReport>) => unwrap(res),
            invalidatesTags: ['ScheduledReports'],
        }),
        updateScheduledReport: builder.mutation<
            ScheduledReport,
            Partial<ScheduledReportInput> & { projectId: string; id: string }
        >({
            query: ({ projectId, id, ...body }) => ({
                url: `${p(projectId)}/scheduled-reports/${encodeURIComponent(id)}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (res: V2Envelope<ScheduledReport>) => unwrap(res),
            invalidatesTags: ['ScheduledReports'],
        }),
        deleteScheduledReport: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/scheduled-reports/${encodeURIComponent(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ScheduledReports'],
        }),
        /** `dryRun` renders the digest without sending; otherwise sends now. */
        sendScheduledReport: builder.mutation<
            DigestRunResult,
            { projectId: string; id: string; dryRun?: boolean }
        >({
            query: a => ({
                url: `${p(a.projectId)}/scheduled-reports/${encodeURIComponent(a.id)}/send`,
                method: 'POST',
                body: a.dryRun ? { dry_run: true } : {},
            }),
            transformResponse: (res: V2Envelope<DigestRunResult>) => unwrap(res),
            // A real send updates last_run/last_status.
            invalidatesTags: (_r, _e, a) => (a.dryRun ? [] : ['ScheduledReports']),
        }),
    }),
})

export const {
    useGetOverviewQuery,
    useGetTimeseriesQuery,
    useGetBreakdownQuery,
    useGetEventsReportQuery,
    useGetEcommerceQuery,
    useGetGoalsReportQuery,
    useGetFunnelReportQuery,
    usePreviewFunnelMutation,
    useGetAttributionQuery,
    useGetRetentionQuery,
    useGetPerformanceQuery,
    useGetErrorsQuery,
    useGetRealtimeQuery,
    useGetUsageQuery,
    useGetRecentEventsQuery,
    useGetProjectConfigQuery,
    useListSubTenantsQuery,
    useListGoalsQuery,
    useCreateGoalMutation,
    useDeleteGoalMutation,
    useListFunnelsQuery,
    useCreateFunnelMutation,
    useDeleteFunnelMutation,
    useListDestinationsQuery,
    useCreateDestinationMutation,
    useUpdateDestinationMutation,
    useDeleteDestinationMutation,
    useTestDestinationMutation,
    useGetDeliveriesQuery,
    useRedeliverMutation,
    useListPortalsQuery,
    useCreatePortalMutation,
    useUpdatePortalMutation,
    useDeletePortalMutation,
    useSetPortalDomainMutation,
    useRefreshPortalDomainMutation,
    useDeletePortalDomainMutation,
    useListPortalUsersQuery,
    useCreatePortalUserMutation,
    useUpdatePortalUserMutation,
    useResendPortalInviteMutation,
    useDeletePortalUserMutation,
    useListScheduledReportsQuery,
    useCreateScheduledReportMutation,
    useUpdateScheduledReportMutation,
    useDeleteScheduledReportMutation,
    useSendScheduledReportMutation,
} = v2Api
